import { useEffect, useMemo, useState } from 'react';

// Material UI
import {
    Box,
    Grid,
    TextField,
    Paper,
    Button,
    ToggleButton as _ToggleButton,
    ToggleButtonGroup,
    styled,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

// Components
import { DatePicker } from 'components/ui/inputs';
import { SubmitButton } from 'components/ui/buttons';

// Utility
import moment from 'moment-timezone';
import { useEvent } from 'context/event';
import { buildEventDayPresets } from 'pages/event/buildEventDayPresets';

const DAY_PRESET_PILL_LIMIT = 10;

export function SalesSummaryDateRangePicker(props) {
    const { onChange, start: currentStart, end: currentEnd } = props;

    const event = useEvent();
    const dayPresets = useMemo(
        () =>
            buildEventDayPresets({
                startDate: event?.start_date,
                endDate: event?.end_date,
                timezone: event?.time_zone,
            }).map((preset) => ({
                ...preset,
                start: `${preset.value}T00:00:00`,
                end: `${preset.value}T23:59:59`,
            })),
        [event?.start_date, event?.end_date, event?.time_zone],
    );
    const showPresetPills = dayPresets.length <= DAY_PRESET_PILL_LIMIT;

    const [state, setState] = useState({
        preset: 'allTime',
        start: null,
        end: null,
    });

    // Set Initial Date Range
    useEffect(() => {
        // Default: All Time (no dates provided)
        if (!currentStart || !currentEnd) {
            setState({
                preset: 'allTime',
                start: null,
                end: null,
            });
            return;
        }

        const dayPreset = dayPresets.find(
            (option) => option.start === currentStart && option.end === currentEnd,
        );

        // One of the Day Presets was found in the URL params
        if (dayPreset) {
            setState({
                preset: dayPreset.value,
                start: dayPreset.start,
                end: dayPreset.end,
            });
            return;
        }

        // Any other dates result in the custom date range selected
        setState({
            preset: 'custom',
            start: currentStart,
            end: currentEnd,
        });
    }, [currentStart, currentEnd, dayPresets]);

    const { preset, start, end } = state;

    const unsavedChanges = start !== currentStart || end !== currentEnd;
    const invalidDates = end < start;

    const handleStartDateChange = (value) =>
        setState({ ...state, start: moment(value).format('YYYY-MM-DDT00:00:00') });
    const handleEndDateChange = (value) =>
        setState({ ...state, end: moment(value).format('YYYY-MM-DDT23:59:59') });

    const handlePresetChange = (presetValue) => {
        const dayPreset = dayPresets.find((option) => option.value === presetValue);

        if (dayPreset) {
            setState({
                preset: presetValue,
                start: dayPreset.start,
                end: dayPreset.end,
            });
            onChange(dayPreset.start, dayPreset.end);
            return;
        }

        switch (presetValue) {
            case 'allTime':
                setState({ preset: 'allTime', start: null, end: null });
                onChange(null, null);
                break;

            case 'today':
                setState({
                    preset: 'today',
                    start: null,
                    end: null,
                });
                onChange(
                    getStartOfDay(moment(), event.time_zone),
                    getEndOfDay(moment(), event.time_zone),
                );
                break;

            case 'custom':
                setState({
                    preset: 'custom',
                    start: getStartOfDay(event.start_date, event.time_zone),
                    end: getEndOfDay(event.end_date, event.time_zone),
                });
                break;

            default:
                break;
        }
    };

    const handleSubmit = () => {
        onChange(start, end);
    };

    return (
        <FilterContainer>
            <Grid container spacing={2}>
                <Grid item xs={12} sm={12}>
                    {showPresetPills ? (
                        <PresetToggleButtonGroup
                            exclusive
                            value={preset}
                            onChange={(_, newValue) => handlePresetChange(newValue)}
                            aria-label="When"
                        >
                            <PresetToggleButton value="allTime">All Time</PresetToggleButton>
                            {dayPresets.map((presetOption) => (
                                <PresetToggleButton
                                    key={presetOption.value}
                                    value={presetOption.value}
                                >
                                    {presetOption.label}
                                </PresetToggleButton>
                            ))}
                            <PresetToggleButton value="custom">Custom</PresetToggleButton>
                        </PresetToggleButtonGroup>
                    ) : (
                        <TextField
                            fullWidth
                            select
                            value={preset}
                            onChange={(e) => handlePresetChange(e.target.value)}
                            label="When"
                            SelectProps={{ native: true }}
                        >
                            <option value="allTime">All Time</option>
                            {dayPresets.map((presetOption) => (
                                <option key={presetOption.value} value={presetOption.value}>
                                    {presetOption.label}
                                </option>
                            ))}
                            <option value="custom">Custom</option>
                        </TextField>
                    )}
                </Grid>
                {preset === 'custom' && (
                    <>
                        <Grid item xs={6} sm={6}>
                            <DatePicker
                                label="Start"
                                value={start}
                                onChange={handleStartDateChange}
                            />
                        </Grid>
                        <Grid item xs={6} sm={6}>
                            <DatePicker label="End" value={end} onChange={handleEndDateChange} />
                        </Grid>
                        <Grid item xs={12} sm={12}>
                            <Box display={'flex'} justifyContent={'flex-end'}>
                                <SubmitButton
                                    disabled={invalidDates || unsavedChanges === false}
                                    onClick={handleSubmit}
                                >
                                    Apply
                                </SubmitButton>
                            </Box>
                        </Grid>
                    </>
                )}
            </Grid>
        </FilterContainer>
    );
}

const FilterContainer = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(2),
}));

const PresetToggleButtonGroup = styled(ToggleButtonGroup)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    width: '100%',
    '& .MuiToggleButtonGroup-grouped': {
        margin: 0,
        border: '1px solid',
        borderColor: theme.palette.divider,
        borderRadius: '999px !important',
        textTransform: 'none',
    },
}));

const PresetToggleButton = styled(_ToggleButton)(({ theme }) => ({
    flex: '0 0 auto',
    height: 20,
    color: theme.palette.text.primary,
    fontSize: '12px',
    '&.Mui-selected': {
        color: theme.palette.success.dark,
        borderColor: theme.palette.success.main,
        backgroundColor: alpha(theme.palette.success.main, 0.12),
    },
    '&.Mui-selected:hover': {
        backgroundColor: alpha(theme.palette.success.main, 0.18),
    },
}));

export const FilterButton = styled(Button)(({ theme }) => ({
    backgroundColor: theme.palette.success.main,
    color: theme.palette.common.white,
}));
FilterButton.defaultProps = { variant: 'contained' };

function getStartOfDay(date, timezone) {
    return moment.tz(date, timezone).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
}

function getEndOfDay(date, timezone) {
    return moment.tz(date, timezone).endOf('day').format('YYYY-MM-DDTHH:mm:ss');
}
